import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import ExportRoutesExcel, { type RouteForExport } from "./ExportRoutesExcel";
import GoogleMapComponent from "./GoogleMapComponent";
import OrderListPanel from "./OrderListPanel";
import OptimizedRoutesPanel from "./OptimizedRoutesPanel";
import { useNavigate } from "react-router-dom";
import ApiConstant from "../../constants/api.constants";
import { SplitIcon, UnsplitIcon } from "./Icons";
import orderServices from "../../services/orders/orderservice";
import routeCodeServices from "../../services/route-codes/routecode.service";
import { Toast } from "primereact/toast";
import type { PolygonManagerHandle } from "./PolygonManager";
import PolygonSelectionPanel from "./PolygonSelectionPanel";
import type { PolygonSelectionSummary } from "./polygon-selection";
import "./smart-map-workspace.css";
import { useMenuUrl } from "../user-menu/menu/navigation";
import { MenuType } from "../../types/enum";
import { calendarDate, dateForCalendar, indiaToday, newRequestId } from "../../utils/delivery-planning";
import SmartMapPlanning, { initialPlanning, planningReady, PlanningConfig } from "./SmartMapPlanning";
import SmartMapDock from "./SmartMapDock";
import RoutePreviewDialog, { RouteReviewContext } from "./RoutePreviewDialog";
import type { OptimizedDriverRoute } from "./getOptimizedRoute";
import { buildDeliveryRoutePlan, estimateRouteMinutes, routeDistanceKm, RouteMetrics } from "./route-preview";
import type { DeliveryRoutePlanInput } from "../../models/delivery-planning";

export interface OrderLocation {
  lat: number;
  lng: number;
  orderId: string;
  customerName: string;
  amount: number;
  taxableAmount?: number;
  createdAt?: Date | null;
  primaryContactNbr?: string | null;
  orderNo?: string | null;
  requestedDate?: Date | string | null;
  requestedBy: string | null;
  customerId?: number | null;
  addressId?: number | null;
  attempts: number;
  isAssigned?: boolean;
  assignedTo?: string | null;
  assignedDate?: string | null;
  assessmentDate?: string | null;
  totalMissingCount?: number;
}

type AgeBucketKey = "new" | "recent" | "old";
type AppliedOrderFilter = { from?: string; to?: string; routeCodes: number[] };
const filterDateLabel = (date?: string) => date
  ? new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
  : "Any date";
const AGE_BUCKETS: { key: AgeBucketKey; label: string; color: string }[] = [
  { key: "new", label: "≤ 3 days", color: "#2e7d32" },
  { key: "recent", label: "4–7 days", color: "#ff9800" },
  { key: "old", label: "≥ 8 days", color: "#d32f2f" },
];

type MapVisibility = {
  hiddenOrderIds: Set<string>;
  showAssigned: boolean;
  assignedVisibility: Map<string, boolean>;
  shownAges: Record<AgeBucketKey, boolean>;
  ageVisibility: Map<string, boolean>;
};
function ageBucket(date?: Date | null, now = Date.now()): AgeBucketKey | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  const days = Math.floor((now - date.getTime()) / (1000 * 60 * 60 * 24));
  return days <= 3 ? "new" : days <= 7 ? "recent" : "old";
}
function hiddenForVisibility(orders: OrderLocation[], state: MapVisibility, includeAges = true): Set<string> {
  const hidden = new Set(state.hiddenOrderIds);
  const now = Date.now();
  for (const order of orders) {
    if (order.isAssigned) {
      if (state.assignedVisibility.get(order.orderId) ?? state.showAssigned) hidden.delete(order.orderId);
      else hidden.add(order.orderId);
    }
    const bucket = ageBucket(order.createdAt, now);
    if (includeAges && bucket && !(state.ageVisibility.get(order.orderId) ?? state.shownAges[bucket])) hidden.add(order.orderId);
  }
  return hidden;
}

function pinSvgUrl(color: string): string {
  const svg = `
    <svg width="36" height="36" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2c-3.866 0-7 3.134-7 7 0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z"
            fill="${color}" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="9" r="2.5" fill="white"/>
    </svg>
  `;
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

const OrderLocations: React.FC = () => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [mappedOrders, setMappedOrders] = useState<OrderLocation[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoadFailed, setOrdersLoadFailed] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const ordersRequest = useRef(0);
  const lastOrdersFilter = useRef<{ routeCodes?: number[]; from?: Date; to?: Date }>({});
  const [totalMissingGPS, setTotalMissingGPS] = useState<number>(0);
  const [polygonSelection, setPolygonSelection] = useState<PolygonSelectionSummary | null>(null);
  const [polygonBusy, setPolygonBusy] = useState(false);
  const [planning, setPlanning] = useState<PlanningConfig>(initialPlanning);
  const [reviewRequest, setReviewRequest] = useState<RouteReviewContext | null>(null);
  const [mainMap, setMainMap] = useState<google.maps.Map | null>(null);
  const [mapLoadState, setMapLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [planningLoading, setPlanningLoading] = useState(true);
  const [routeCodesLoading, setRouteCodesLoading] = useState(true);
  const [routeCodesFailed, setRouteCodesFailed] = useState(false);
  const [reviewMapLoading, setReviewMapLoading] = useState(false);
  const [reviewIncludedIds, setReviewIncludedIds] = useState<string[]>([]);
  const [addOrderRequest, setAddOrderRequest] = useState<{ orderId: string; token: number } | null>(null);
  const [drawingControlHost, setDrawingControlHost] = useState<HTMLDivElement | null>(null);
  const [gpsControlHost, setGpsControlHost] = useState<HTMLDivElement | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState<AppliedOrderFilter | null>(null);
  const [planningOpen, setPlanningOpen] = useState(true);
  const [routePanelOpen, setRoutePanelOpen] = useState(true);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarBottom, setToolbarBottom] = useState(170);
  useEffect(() => {
    const update = () => setToolbarBottom(Math.ceil((toolbarRef.current?.offsetTop ?? 8) + (toolbarRef.current?.getBoundingClientRect().height ?? 0)) + 8);
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    if (toolbarRef.current) observer?.observe(toolbarRef.current);
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); };
  }, []);
  const [previewOrderIds, setPreviewOrderIds] = useState<string[]>([]);
  const reviewResolve = useRef<((routes: OptimizedDriverRoute[] | null) => void) | null>(null);
  const [previewMetrics, setPreviewMetrics] = useState<RouteMetrics[]>([]);
  const reviewRoutes = useCallback((context: RouteReviewContext) => new Promise<OptimizedDriverRoute[] | null>(resolve => {
    reviewResolve.current?.(null); reviewResolve.current = resolve;
    setPreviewMetrics([]); setPreviewOrderIds([]); setReviewRequest(context); setRoutePanelOpen(true);
    setReviewMapLoading(true); setReviewIncludedIds(context.orders.map(order => String(order.orderId))); setAddOrderRequest(null);
    if (window.innerWidth <= 900) setSplitView(false);
    setResizeTick(t => t + 1);
  }), []);
  const closeReview = useCallback((routes: OptimizedDriverRoute[] | null) => {
    reviewResolve.current?.(routes); reviewResolve.current = null;
    setReviewRequest(null); setPreviewMetrics([]); setPreviewOrderIds([]); setResizeTick(t => t + 1);
    setReviewMapLoading(false); setReviewIncludedIds([]); setAddOrderRequest(null);
  }, []);
  useEffect(() => () => { reviewResolve.current?.(null); reviewResolve.current = null; }, []);
  const [routeCodeOptions, setRouteCodeOptions] = useState<any[]>([]);
  const [selectedRouteCodes, setSelectedRouteCodes] = useState<number[]>([]);
  const [routesForExport, setRoutesForExport] = useState<RouteForExport[]>([]);
  const [splitView, setSplitView] = useState<boolean>(false);
  const [resizeTick, setResizeTick] = useState(0);

  // NEW: Optimized view pane
  const [optPanelOpen, setOptPanelOpen] = useState<boolean>(false);
  const [optimizedDrivers, setOptimizedDrivers] = useState<
    { driverId: number; driverName: string; orderCount: number; totalDistanceKm: number; totalTaxableAmount: number; }[]
  >([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);

  const [hiddenOrderIds, setHiddenOrderIds] = useState<Set<string>>(new Set());
  const [showAssigned, setShowAssigned] = useState(false);
  const [assignedVisibility, setAssignedVisibility] = useState<Map<string, boolean>>(new Map());
  const [shownAges, setShownAges] = useState<Record<AgeBucketKey, boolean>>({ new: true, recent: true, old: true });
  const [ageVisibility, setAgeVisibility] = useState<Map<string, boolean>>(new Map());
  const visibilityBackup = useRef<MapVisibility | null>(null);
  const [focusedOrderId, setFocusedOrderId] = useState<string | null>(null);
  const toastRef = useRef<Toast>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingDeliveries, setPendingDeliveries] = useState<Parameters<typeof orderServices.importDriverList1>[0]>([]);
  const [pendingRoutePlan, setPendingRoutePlan] = useState<DeliveryRoutePlanInput | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(() => dateForCalendar(indiaToday()));
  const assignmentRequest = useRef<{ key: string; id: string } | null>(null);
  const navigate = useNavigate();
  const getMenuUrl = useMenuUrl();

  // Expose reverse API from PolygonManager via GoogleMapComponent
  const polyMgrRef = useRef<PolygonManagerHandle>(null);

  const baseHiddenOrderIds = useMemo(() => hiddenForVisibility(mappedOrders,
    { hiddenOrderIds, showAssigned, assignedVisibility, shownAges, ageVisibility }, false),
    [mappedOrders, hiddenOrderIds, showAssigned, assignedVisibility, shownAges, ageVisibility]);
  const effectiveHiddenOrderIds = useMemo(() => hiddenForVisibility(mappedOrders,
    { hiddenOrderIds, showAssigned, assignedVisibility, shownAges, ageVisibility }),
    [mappedOrders, hiddenOrderIds, showAssigned, assignedVisibility, shownAges, ageVisibility]);
  const assignedOrders = mappedOrders.filter(o => o.isAssigned);
  const allAssignedShown = assignedOrders.length > 0 && assignedOrders.every(o => !baseHiddenOrderIds.has(o.orderId));
  const visibleOrders = useMemo(
    () => mappedOrders.filter(o => !effectiveHiddenOrderIds.has(o.orderId)),
    [mappedOrders, effectiveHiddenOrderIds]
  );
  useEffect(() => {
    if (reviewRequest && reviewRequest.orders.some(order => !visibleOrders.some(current =>
      current.orderId === order.orderId && !current.isAssigned && current.lat === order.lat && current.lng === order.lng))) closeReview(null);
  }, [visibleOrders, reviewRequest, closeReview]);
  const appliedMetrics: RouteMetrics[] = routesForExport.flatMap((r, index) => reviewRequest?.replacingRouteIndex === index ? [] : [{ driverId: r.driverId ?? index,
    distanceKm: r.totalDistanceKm ?? 0, drivingMinutes: r.drivingMinutes ?? 0, orderCount: r.ordersInRoute.length }]);
  const routeMetrics = [...appliedMetrics, ...previewMetrics];
  const distanceKm = routeDistanceKm(routeMetrics);
  const timeAvailable = routesForExport.every(route => Number.isFinite(route.drivingMinutes));
  const estimatedMinutes = estimateRouteMinutes(routeMetrics, planning.deliveryMinutes ?? 0, planning.breakMinutes ?? 0);
  const roundedEstimatedMinutes = Math.ceil(estimatedMinutes);
  const changePlanning = (next: PlanningConfig) => {
    if (next.assignments !== planning.assignments && (polygonSelection || routesForExport.length || reviewRequest)) {
      closeReview(null); polyMgrRef.current?.clearRoutes(); setRoutesForExport([]); setOptimizedDrivers([]);
    }
    setPlanning(next);
  };
  useEffect(() => {
    // Optimization can finish after a group was hidden; discard that late preview too.
    const blocked = new Set(mappedOrders.filter(o => {
      const bucket = ageBucket(o.createdAt);
      return bucket && !(ageVisibility.get(o.orderId) ?? shownAges[bucket]);
    }).map(o => o.orderId));
    if (routesForExport.some(r => r.ordersInRoute.some(o => blocked.has(String(o.orderId ?? ""))))) {
      polyMgrRef.current?.clearRoutes(); setRoutesForExport([]); setOptimizedDrivers([]);
    }
  }, [mappedOrders, shownAges, ageVisibility, routesForExport]);

  const getAgeBucketCounts = useCallback(() => {
    const now = Date.now();
    return mappedOrders.filter(o => !baseHiddenOrderIds.has(o.orderId)).reduce(
      (acc, order) => {
        const bucket = ageBucket(order.createdAt, now);
        if (bucket) acc[bucket]++;
        return acc;
      },
      { new: 0, recent: 0, old: 0 }
    );
  }, [mappedOrders, baseHiddenOrderIds]);

  const ageBucketCounts = useMemo(() => getAgeBucketCounts(), [getAgeBucketCounts]);

  // Freeze the optimized assignments before asking for their delivery date.
  const openSaveDialog = () => {
    if (savingRef.current) return;
    if (!routesForExport.length || reviewRequest || polygonBusy || !planningReady(planning)) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Nothing to save",
        detail: "No routes/orders to send.",
        life: 2500,
      });
      return;
    }

    const payload = routesForExport.flatMap((r) =>
      (r.ordersInRoute ?? []).map((o) => ({
        orderID: Number(o.orderId) || 0,
        primaryContactNumber: (r.phonenumber ?? "").trim(),
        sequenceNo: Number(o.sequence ?? 0),
        vehicleNo: r.vehicleNo ?? "",
      }))
    );

    if (!payload.length) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Nothing to save",
        detail: "No orders present in routes.",
        life: 2500,
      });
      return;
    }

    try { setPendingRoutePlan(buildDeliveryRoutePlan(routesForExport, planning.deliveryMinutes, planning.breakMinutes)); }
    catch (error) {
      toastRef.current?.show({ severity: "warn", summary: "Route estimates", detail: error instanceof Error ? error.message : "Rebuild the route before saving." });
      return;
    }
    setPendingDeliveries(payload);
    setDeliveryDate(dateForCalendar(indiaToday()));
    setSaveDialogOpen(true);
  };

  const handleSaveToApi = async () => {
    if (savingRef.current || !saveDialogOpen || !pendingDeliveries.length || !pendingRoutePlan) return;
    if (!deliveryDate || calendarDate(deliveryDate) < indiaToday()) {
      toastRef.current?.show({ severity: "warn", summary: "Delivery date", detail: "Select today or a future delivery date." });
      return;
    }
    const payload = pendingDeliveries;
    savingRef.current = true;

    try {
      setSaving(true);
      const date = calendarDate(deliveryDate);
      const key = JSON.stringify({ date, payload, routePlan: pendingRoutePlan });
      if (assignmentRequest.current?.key !== key) assignmentRequest.current = { key, id: newRequestId() };
      const response = await orderServices.importDriverList1(payload, date, assignmentRequest.current.id, pendingRoutePlan);
      if (response?.isSuccess) {
        assignmentRequest.current = null;
        setSaveDialogOpen(false);
        setPendingDeliveries([]);
        setPendingRoutePlan(null);
        setAssignedVisibility(prev => {
          const next = new Map(prev);
          payload.forEach(o => next.delete(String(o.orderID)));
          return next;
        });
        polyMgrRef.current?.clearRoutes();
        setRoutesForExport([]);
        setOptimizedDrivers([]);
        toastRef.current?.show({
          severity: "success",
          summary: "Saved",
          detail: response.message || "Delivery info saved successfully.",
          life: 3500,
        });
        await fetchOrderIds(selectedRouteCodes, fromDate ?? undefined, toDate ?? undefined);
      } else {
        toastRef.current?.show({
          severity: "error",
          summary: "Save failed",
          detail: response?.message || "Failed to save delivery info.",
          life: 6000,
        });
      }
    } catch (err: unknown) {
      const detail = axios.isAxiosError<{ message?: string; error?: string }>(err)
        ? err.response?.data?.message || err.response?.data?.error || err.message
        : err instanceof Error ? err.message : "Something went wrong.";
      toastRef.current?.show({
        severity: "error",
        summary: "Save failed",
        detail,
        life: 6000,
      });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Helpers
  const formatDateInIST = useCallback((date: Date): string => {
    const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    const istDate = new Date(date.getTime() - tzOffsetMs);
    return istDate.toISOString().split("T")[0];
  }, []);

  const getLast30DaysRange = (): { from: Date; to: Date } => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    return { from, to };
  };

  const pickCreatedAt = (order: any): Date | null => {
    const raw = order.createdAt ?? order.createdDate ?? order.orderDate ?? order.date ?? null;
    if (!raw) return null;
    try {
      if (typeof raw === "number") return new Date(raw > 1e12 ? raw : raw * 1000);
      return new Date(raw);
    } catch {
      return null;
    }
  };

  const pickExpectedAt = (order: any): Date | string | null => order.requestedDate ?? null;

  const pickPhone = (order: any): string | null =>
    order.primaryContactNbr ?? order.mobile ?? order.mobileNumber ?? order.phone ?? order.phoneNumber ?? null;

  const pickOrderNo = (order: any): string | null =>
    order.orderNumber ?? order.orderNo ?? order.orderID?.toString?.() ?? null;

  // Fetch
  const fetchOrderIds = useCallback(
    async (routeCodes?: number[], from?: Date, to?: Date) => {
      const request = ++ordersRequest.current;
      lastOrdersFilter.current = { routeCodes: routeCodes?.slice(), from, to };
      setOrdersLoading(true);
      setOrdersLoadFailed(false);
      try {
        const filters: AppliedOrderFilter = { routeCodes: routeCodes?.slice() ?? [],
          from: from ? formatDateInIST(from) : undefined, to: to ? formatDateInIST(to) : undefined };
        const resp: any = await orderServices.getOpenOrdersForMap(
          filters.routeCodes.length > 0 ? filters.routeCodes : undefined, filters.from, filters.to
        );
        if (request !== ordersRequest.current) return;

        const ordersRaw: any[] = Array.isArray(resp) ? resp : resp?.orders ?? [];

        const missingCount: number = Array.isArray(resp)
          ? ordersRaw.reduce((acc, o) => {
            const loc = o.gpsLocation as string | null | undefined;
            return acc + (!loc || !loc.includes(",") ? 1 : 0);
          }, 0)
          : resp?.totalMissingGPS ?? 0;

        const parsed: OrderLocation[] = ordersRaw
          .filter((o) => o.gpsLocation && (o.gpsLocation as string).includes(","))
          .map((o) => {
            const [latStr, lngStr] = (o.gpsLocation as string).split(",");
            return {
              lat: parseFloat(latStr.trim()),
              lng: parseFloat(lngStr.trim()),
              orderId: o.orderID?.toString?.() ?? "",
              customerName: o.userName,
              amount: 0,
              createdAt: pickCreatedAt(o),
              primaryContactNbr: pickPhone(o),
              orderNo: pickOrderNo(o),
              taxableAmount: o.taxableAmount ?? 0,
              requestedDate: pickExpectedAt(o),
              requestedBy: o.requestedBy ?? null,
              customerId: o.customerID ?? o.customerId ?? o.userID ?? o.userId ?? null,
              addressId: o.userAddressID ?? o.userAddressId ?? null,
              totalMissingCount: missingCount,
              attempts: o.attempts ?? 0,
              isAssigned: o.isAssigned === true,
              assignedTo: o.assignedTo ?? null,
              assignedDate: o.assignedDate ?? null,
              assessmentDate: o.assessmentDate ?? null,
            };
          });

        setMappedOrders(parsed);
        setAppliedFilter(filters);
        setOrdersLoaded(true);
        setTotalMissingGPS(missingCount);
        setAssignedVisibility(prev => new Map(Array.from(prev).filter(([id]) => parsed.some(o => o.orderId === id && o.isAssigned))));
        setAgeVisibility(prev => new Map(Array.from(prev).filter(([id]) => parsed.some(o => o.orderId === id))));

        // clean hidden ids
        setHiddenOrderIds((prev) => {
          const next = new Set(prev);
          const validIds = new Set(parsed.map((x) => x.orderId));
          next.forEach((id) => {
            if (!validIds.has(id)) next.delete(id);
          });
          return next;
        });
      } catch {
        if (request === ordersRequest.current) setOrdersLoadFailed(true);
      } finally {
        if (request === ordersRequest.current) setOrdersLoading(false);
      }
    },
    [formatDateInIST]
  );

  // Route codes
  useEffect(() => {
    const loadRouteCodes = async () => {
      try {
        const codes = await routeCodeServices.getRouteCodes();
        setRouteCodeOptions(codes || []);
      } catch (error) {
        setRouteCodesFailed(true);
        console.error("Error loading route codes:", error);
      } finally {
        setRouteCodesLoading(false);
      }
    };
    loadRouteCodes();
  }, []);

  // Initial load
  useEffect(() => {
    const { from, to } = getLast30DaysRange();
    setFromDate(from);
    setToDate(to);
    fetchOrderIds(selectedRouteCodes, from, to);
    return () => { ++ordersRequest.current; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrderIds]);

  const handleFilter = useCallback(() => {
    if (fromDate && toDate) {
      fetchOrderIds(selectedRouteCodes, fromDate, toDate);
    }
  }, [fromDate, toDate, selectedRouteCodes, fetchOrderIds]);

  const handleRouteCodeChange = (e: MultiSelectChangeEvent) => {
    setSelectedRouteCodes(e.value as number[]);
  };

  const handleReset = useCallback(() => {
    const { from, to } = getLast30DaysRange();
    setFromDate(from);
    setToDate(to);
    setSelectedRouteCodes([]);
    setHiddenOrderIds(new Set());
    setShowAssigned(false);
    setAssignedVisibility(new Map());
    setShownAges({ new: true, recent: true, old: true }); setAgeVisibility(new Map()); visibilityBackup.current = null;
    fetchOrderIds([], from, to);
    window.location.reload();
  }, [fetchOrderIds]);

  const toggleOrderVisibility = (orderId: string, show: boolean) => {
    const bucket = ageBucket(mappedOrders.find(o => o.orderId === orderId)?.createdAt);
    setAgeVisibility(prev => {
      const next = new Map(prev);
      if (show && bucket && !shownAges[bucket]) next.set(orderId, true);
      else next.delete(orderId);
      return next;
    });
    if (mappedOrders.some(o => o.orderId === orderId && o.isAssigned)) {
      setAssignedVisibility(prev => new Map(prev).set(orderId, show));
      return;
    }
    setHiddenOrderIds((prev) => {
      const next = new Set(prev);
      if (show) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const showAllOnMap = () => {
    visibilityBackup.current = { hiddenOrderIds: new Set(hiddenOrderIds), showAssigned,
      assignedVisibility: new Map(assignedVisibility), shownAges: { ...shownAges }, ageVisibility: new Map(ageVisibility) };
    setHiddenOrderIds(new Set()); setShowAssigned(true); setAssignedVisibility(new Map());
    setShownAges({ new: true, recent: true, old: true }); setAgeVisibility(new Map());
  };
  const clearPreviewFor = (hiddenIds: Set<string>) => {
    if (routesForExport.some(r => r.ordersInRoute.some(o => hiddenIds.has(String(o.orderId ?? ""))))) {
      polyMgrRef.current?.clearRoutes(); setRoutesForExport([]); setOptimizedDrivers([]);
    }
  };
  const restoreMapVisibility = (hidden: Set<string>) => {
    const previous = visibilityBackup.current;
    if (!previous) { mappedOrders.forEach(o => toggleOrderVisibility(o.orderId, !hidden.has(o.orderId))); return; }
    clearPreviewFor(hiddenForVisibility(mappedOrders, previous));
    setHiddenOrderIds(previous.hiddenOrderIds); setShowAssigned(previous.showAssigned);
    setAssignedVisibility(previous.assignedVisibility); setShownAges(previous.shownAges); setAgeVisibility(previous.ageVisibility);
    visibilityBackup.current = null;
  };
  const toggleAgeBucket = (bucket: AgeBucketKey) => {
    const ids = new Set(mappedOrders.filter(o => ageBucket(o.createdAt) === bucket).map(o => o.orderId));
    if (shownAges[bucket]) clearPreviewFor(ids);
    setShownAges(prev => ({ ...prev, [bucket]: !prev[bucket] }));
    setAgeVisibility(prev => new Map(Array.from(prev).filter(([id]) => !ids.has(id))));
  };
  const toggleAssignedOrders = () => {
    if (allAssignedShown && routesForExport.some(r => r.ordersInRoute.some(o => assignedOrders.some(a => a.orderId === String(o.orderId ?? ""))))) {
      polyMgrRef.current?.clearRoutes(); setRoutesForExport([]); setOptimizedDrivers([]);
    }
    setShowAssigned(!allAssignedShown); setAssignedVisibility(new Map());
  };

  // Resize map when right panel toggles
  useEffect(() => {
    setResizeTick((t) => t + 1);
  }, [splitView, optPanelOpen]);

  // Keep optimized drivers + selected
  useEffect(() => {
    if (!optimizedDrivers.length) {
      setSelectedDriverId(null);
      setOptPanelOpen(false);
      return;
    }
    if (selectedDriverId == null) {
      setSelectedDriverId(optimizedDrivers[0].driverId);
    } else {
      const stillExists = optimizedDrivers.some((d) => d.driverId === selectedDriverId);
      if (!stillExists) setSelectedDriverId(optimizedDrivers[0].driverId);
    }
  }, [optimizedDrivers, selectedDriverId]);

  const handleReverseSelected = async () => {
    if (selectedDriverId == null) return;
    await polyMgrRef.current?.reverseDriverRoute(selectedDriverId);
    // routesForExport auto-updates via onRoutesForExportChange
  };

  const toggleOpenOrders = () => {
    if (!splitView && window.innerWidth <= 900) setRoutePanelOpen(false);
    setSplitView(v => !v);
    setResizeTick(t => t + 1);
  };
  const toggleRouteOrders = () => {
    if (!routePanelOpen && window.innerWidth <= 900) setSplitView(false);
    setRoutePanelOpen(v => !v);
    setResizeTick(t => t + 1);
  };

  const routeTargets = useMemo(() => reviewRequest ? [{ value: "review", label: "Current route preview" }]
    : [...(polygonSelection ? [{ value: "polygon", label: "Current polygon" }] : []),
      ...optimizedDrivers.map((driver, index) => ({ value: String(index), label: `${driver.driverName} · Route ${index + 1}` }))], [reviewRequest, polygonSelection, optimizedDrivers]);
  const popupActions = {
    routeTargets, includedIds: [...reviewIncludedIds, ...routesForExport.flatMap((route, index) => reviewRequest?.replacingRouteIndex === index ? [] : route.ordersInRoute.map(order => String(order.orderId)))],
    routeActionDisabled: saving || saveDialogOpen || (polygonBusy && !reviewRequest),
    onAddToRoute: async (orderId: string, target: string) => {
      const order = mappedOrders.find(item => String(item.orderId) === orderId);
      if (!order || order.isAssigned || !Number.isFinite(order.lat) || !Number.isFinite(order.lng) || saving || saveDialogOpen) throw new Error("The order is unavailable.");
      if (target === "review" && reviewRequest) {
        setReviewMapLoading(true);
        setAddOrderRequest({ orderId, token: Date.now() });
        setReviewRequest(previous => previous ? { ...previous, orders: previous.orders.some(item => String(item.orderId) === orderId)
          ? previous.orders : [...previous.orders, order] } : previous);
        setRoutePanelOpen(true);
      } else {
        if (!polyMgrRef.current?.addOrderToRoute) throw new Error("Draw a polygon first.");
        void polyMgrRef.current.addOrderToRoute(orderId, target === "polygon" ? undefined : Number(target)).catch(() => {
          toastRef.current?.show({ severity: "error", summary: "Order not added", detail: "Could not update the route. Please retry." });
        });
      }
    },
  };
  const startupLoading = (!ordersLoaded && !ordersLoadFailed) || planningLoading || routeCodesLoading || mapLoadState === "loading";
  const drawingRoute = reviewRequest ? reviewMapLoading : polygonBusy;
  return (
    <div className="smart-map-page">
      <div className="smart-map-canvas" style={{ "--smart-map-toolbar-bottom": `${toolbarBottom}px` } as React.CSSProperties}>
      {/* Toolbar */}
      <div className="smart-map-toolbar" ref={toolbarRef}>
        <div className="smart-map-toolbar-controls">
        <Toast ref={toastRef} />
        <div id="smart-map-filters" className="smart-map-filter-panel" role="region" aria-label="Order filters" hidden={!filtersOpen}
          onKeyDown={e => { if (e.key === "Escape") setFiltersOpen(false); }}>
          <strong>Order filters</strong>
          <p className="smart-map-filter-hint">Choose an order date range and route codes, then apply to update the map.</p>
          {routeCodesFailed && <p className="smart-map-filter-hint" role="alert">Route code options are unavailable. Refresh the page to retry; date filtering is still available.</p>}
        <div className="smart-map-filter-field"><label htmlFor="map-filter-from">From date</label>
        <Calendar
          inputId="map-filter-from"
          value={fromDate}
          onChange={(e) => {
            const selectedDate = e.value as Date;
            setFromDate(selectedDate);
            if (toDate && selectedDate && toDate < selectedDate) setToDate(null);
          }}
          placeholder="From Date"
          dateFormat="dd/mm/yy"
          showIcon
          readOnlyInput
        />
        </div>
        <div className="smart-map-filter-field"><label htmlFor="map-filter-to">To date</label>
        <Calendar
          inputId="map-filter-to"
          value={toDate}
          onChange={(e) => setToDate(e.value as Date)}
          placeholder="To Date"
          dateFormat="dd/mm/yy"
          showIcon
          minDate={fromDate || undefined}  // can't pick before fromDate
          maxDate={new Date()}             // can't pick after today
          readOnlyInput
        />
        </div>

        <div className="smart-map-filter-field smart-map-filter-routes"><label htmlFor="map-filter-routes">Route codes</label>
          <MultiSelect
            inputId="map-filter-routes"
            aria-label="Filter route codes"
            name="RouteCode"
            options={routeCodeOptions}
            optionLabel="name"
            optionValue="routeCodeID"
            value={selectedRouteCodes}
            display="chip"
            maxSelectedLabels={2}
            onChange={handleRouteCodeChange}
            placeholder="All route codes"
            style={{ width: "100%" }}
            filter
          />
        </div>

          <div className="smart-map-filter-actions">
            <Button label="Apply filters" icon="pi pi-check" disabled={!fromDate || !toDate}
              onClick={() => { handleFilter(); setFiltersOpen(false); }} />
            <Button label="Close filters" outlined onClick={() => setFiltersOpen(false)} />
          </div>
        </div>
        <div className="smart-map-primary-controls">
        <Button
          icon="pi pi-filter"
          aria-label="Filter"
          onClick={() => setFiltersOpen(v => !v)}
          aria-expanded={filtersOpen} aria-controls="smart-map-filters"
          tooltip={"Filter"}
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-refresh"
          onClick={handleReset}
          tooltip={"Reset"}
          tooltipOptions={{ position: "top" }}
        />

        {/* Existing Split (Orders list) */}
        <Button
          icon={splitView ? <UnsplitIcon size={20} /> : <SplitIcon size={20} />}
          onClick={toggleOpenOrders}
          aria-label="Toggle open orders"
          aria-pressed={splitView}
          tooltip={splitView ? "Exit Split" : "Split View"}
          tooltipOptions={{ position: "top" }}
        />

        {/* NEW: Optimized View (enabled only when routes exist) */}
        <Button
          icon="pi pi-car"
          onClick={() => { setOptPanelOpen(v => !v); setRoutePanelOpen(true); }}
          aria-label="Toggle optimized routes"
          disabled={routesForExport.length === 0}
          tooltip={
            routesForExport.length === 0
              ? "Run optimization first"
              : optPanelOpen
                ? "Hide Optimized View"
                : "Show Optimized View"
          }
          tooltipOptions={{ position: "top" }}
        />

        <Button onClick={() => { const url = getMenuUrl(MenuType.UnassignedOrders); if (url) window.open(`${url}?tab=geo`, "_blank"); }} iconPos="left" severity="danger" disabled={totalMissingGPS === 0}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginRight: "6px" }}
          >
            <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"></path>
            <circle cx="12" cy="9" r="2.5"></circle>
            <line x1="3" y1="3" x2="21" y2="21"></line>
          </svg>
          {` GPS: ${totalMissingGPS}`}
        </Button>

        <ExportRoutesExcel
          routes={routesForExport}
          label="Export to Excel"
          style={{ padding: "0.6rem 0.9rem" }}
        />

        <Button
          icon="pi pi-file-export"
          aria-label="Save to Delivery"
          onClick={openSaveDialog}
          disabled={!routesForExport.length || saving || polygonBusy || !!reviewRequest || !planningReady(planning)}
          loading={saving}
          tooltip={saving ? "Saving..." : "Save to delivery"}
          tooltipOptions={{ position: "top" }}
        />

        <div className="smart-map-drawing-controls" ref={setDrawingControlHost} />
        <div className="smart-map-age-controls">
            {AGE_BUCKETS.map((b) => (
              <Button type="button"
                key={b.key}
                aria-label={b.label} aria-pressed={shownAges[b.key]}
                disabled={saving || saveDialogOpen} onClick={() => toggleAgeBucket(b.key)}
                tooltip={`${shownAges[b.key] ? "Hide" : "Show"} ${b.label} orders on the map`} tooltipOptions={{ position: "top" }}
                style={{
                  background: shownAges[b.key] ? "#f3f4f6" : "#fff",
                  color: "#111827",
                  border: `1px solid ${shownAges[b.key] ? "#e5e7eb" : b.color}`,
                  padding: "0.4rem 0.7rem",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, lineHeight: 1 }}>
                  <img src={pinSvgUrl(b.color)} alt="" style={{ height: 22, width: "auto", display: "block" }} />
                  <span>{`${b.label} (${ageBucketCounts[b.key]})`}</span>
                  <i className={shownAges[b.key] ? "pi pi-eye" : "pi pi-eye-slash"} aria-hidden="true" />
                </span>
              </Button>
            ))}
        </div>
        <div className="smart-map-gps-control" ref={setGpsControlHost} />
        </div>
        </div>
        <div className="smart-map-applied-filters" role="region" aria-label="Applied order filters">
          <strong><i className="pi pi-filter" aria-hidden="true" /> Applied filters</strong>
          {appliedFilter ? <>
            <span>{filterDateLabel(appliedFilter.from)} – {filterDateLabel(appliedFilter.to)}</span>
            <span>{appliedFilter.routeCodes.length ? appliedFilter.routeCodes.map(id =>
              routeCodeOptions.find(code => code.routeCodeID === id)?.name || `Route ${id}`).join(", ") : "All route codes"}</span>
          </> : <span>{ordersLoadFailed ? "No filters loaded" : "Loading filters…"}</span>}
        </div>
      </div>
      <aside className="smart-map-summary" aria-label="Map summary">
        <section className="smart-map-planning-card" aria-label="Route setup">
          <div className="smart-map-planning-heading"><strong><i className="pi pi-sliders-h" aria-hidden="true" /> Route setup</strong>
            <button type="button" aria-label={planningOpen ? "Minimize route setup" : "Expand route setup"}
              aria-expanded={planningOpen} aria-controls="smart-map-planning-fields" onClick={() => setPlanningOpen(value => !value)}>
              <i className={`pi ${planningOpen ? "pi-minus" : "pi-plus"}`} aria-hidden="true" />
            </button>
          </div>
          <div id="smart-map-planning-fields" className="smart-map-planning-row" hidden={!planningOpen}>
            <SmartMapPlanning value={planning} onChange={changePlanning} disabled={saving || saveDialogOpen} lockAssignments={polygonBusy} onLoadingChange={setPlanningLoading} />
          </div>
          {!planningOpen && <p className="smart-map-planning-collapsed">{planning.assignments.length} executive(s) · {planning.deliveryMinutes ?? "—"} min/order · {planning.breakMinutes ?? "—"} min break</p>}
        </section>
        <div className="smart-map-route-metrics" aria-label="Route estimates" aria-live="polite">
          <span><small>Route distance</small><strong>{routeMetrics.length ? `${distanceKm.toFixed(2)} km` : "—"}</strong></span>
          <span><small>Estimated total route time</small><strong>{routeMetrics.length && timeAvailable && planning.deliveryMinutes !== null && planning.breakMinutes !== null
            ? <>{roundedEstimatedMinutes} min <small className="smart-map-time-hours">({Math.floor(roundedEstimatedMinutes / 60)} hr {roundedEstimatedMinutes % 60} min)</small></> : "—"}</strong></span>
          <small>Driving + delivery time per order + one break per route. Distance includes start and end points.</small>
        </div>
        <PolygonSelectionPanel selection={polygonSelection} missingGPS={totalMissingGPS}
          ready={planningReady(planning)}
          busy={polygonBusy || saving || saveDialogOpen} canViewMissingGPS={!!getMenuUrl(MenuType.UnassignedOrders)}
          onViewMissingGPS={() => {
            const url = getMenuUrl(MenuType.UnassignedOrders);
            if (url) window.open(`${url}?tab=geo`, "_blank", "noopener,noreferrer");
          }}
          onAssign={() => { void polyMgrRef.current?.assignSelection(); }} />
            <Button label={allAssignedShown ? "Hide assigned" : "Show assigned"}
              icon={allAssignedShown ? "pi pi-eye-slash" : "pi pi-eye"} size="small"
              aria-pressed={allAssignedShown} disabled={assignedOrders.length === 0 || saving || saveDialogOpen}
              style={{ paddingInline: "0.9rem", flexShrink: 0, backgroundColor: "#15583f", borderColor: "#104b35", color: "#fff", opacity: 1 }}
              onClick={toggleAssignedOrders} />
      </aside>

      {startupLoading && <div className="smart-map-loading-overlay smart-map-startup-loader" role="status" aria-label="Loading Smart Map">
        <div><i className="pi pi-spinner pi-spin" aria-hidden="true" /><strong>Loading Smart Map</strong><span>Preparing the map, open orders and route options…</span></div>
      </div>}
      {!startupLoading && drawingRoute && <div className="smart-map-loading-overlay smart-map-route-loader" role="status" aria-label="Loading road route">
        <div><i className="pi pi-spinner pi-spin" aria-hidden="true" /><strong>Preparing your route</strong><span>Optimizing orders and drawing the road route…</span></div>
      </div>}
      {!startupLoading && ordersLoading && <div role="status" aria-label="Order loading" className="smart-map-load-status">Loading open orders…</div>}
      {mapLoadState === "error" && <div role="alert" className="smart-map-load-error">Unable to load Google Maps.
        <Button label="Retry map" size="small" onClick={() => window.location.reload()} /></div>}
      {ordersLoadFailed && <div role="alert" className="smart-map-load-error">
        <span>Unable to load open orders. {ordersLoaded ? "Showing previously loaded orders; the latest filters have not been applied." : "Please retry."}</span>
        <Button label="Retry loading orders" icon="pi pi-refresh" size="small" outlined onClick={() => {
          const { routeCodes, from, to } = lastOrdersFilter.current;
          void fetchOrderIds(routeCodes, from, to);
        }} />
      </div>}

      <Dialog visible={saveDialogOpen} header="Planned delivery date" modal style={{ width: "min(95vw, 480px)" }}
        closable={!saving} onHide={() => { if (!savingRef.current) setSaveDialogOpen(false); }}
        footer={<div className="flex justify-content-end gap-2">
          <Button label="Cancel" outlined disabled={saving} onClick={() => setSaveDialogOpen(false)} />
          <Button label="Confirm assignment" icon="pi pi-check" loading={saving}
            disabled={saving || !deliveryDate || calendarDate(deliveryDate) < indiaToday()}
            onClick={() => void handleSaveToApi()} />
        </div>}>
        <p>Assign {pendingDeliveries.length} orders to the selected delivery executives on this date.</p>
        <label htmlFor="planned-delivery-date" className="block font-semibold mb-2">Delivery date</label>
        <Calendar inputId="planned-delivery-date" value={deliveryDate} className="w-full"
          onChange={e => setDeliveryDate(e.value as Date | null)}
          minDate={dateForCalendar(indiaToday())} dateFormat="dd/mm/yy" showIcon readOnlyInput disabled={saving} />
        <p>Choose today or a future date (IST). These orders appear in Delivery Assessment only on the selected date.</p>
      </Dialog>

      <div className="smart-map-surface">
        <div className="smart-map-map">
          <GoogleMapComponent
            lat={12.2958}
            lng={76.6394}
            orders={visibleOrders}
            onMapReady={setMainMap} previewOrderIds={previewOrderIds}
            onLoadStateChange={setMapLoadState} popupActions={popupActions}
            drawingControlHost={drawingControlHost} gpsControlHost={gpsControlHost}
            totalOrderCount={mappedOrders.length}
            focusOrderId={focusedOrderId}
            onFocused={() => setFocusedOrderId(null)}
            resizeSignal={resizeTick}
            onRoutesForExportChange={setRoutesForExport}
            onOptimizedDriversChange={setOptimizedDrivers}
            polygonManagerRef={polyMgrRef}
            onSelectionChange={setPolygonSelection}
            onSelectionBusyChange={setPolygonBusy}
            routeAssignments={planning.assignments} reviewRoutes={reviewRoutes}
            drawingEnabled={planningReady(planning) && !saving && !saveDialogOpen && !polygonBusy}
            autoReview
          />
        </div>

        <div className="smart-map-docks">
          {(reviewRequest || optPanelOpen) && <SmartMapDock id="smart-map-route-orders" label="Route orders" open={routePanelOpen} onToggle={toggleRouteOrders}>
            {reviewRequest ? <RoutePreviewDialog {...reviewRequest}
              embedded map={mainMap} onClose={closeReview} onMetrics={setPreviewMetrics} onSelection={setPolygonSelection}
              onLoadingChange={setReviewMapLoading} onActiveOrderIds={setReviewIncludedIds} addOrderRequest={addOrderRequest}
              onRoutedOrderIds={setPreviewOrderIds} /> : <OptimizedRoutesPanel
                drivers={optimizedDrivers} selectedDriverId={selectedDriverId} onChangeDriver={setSelectedDriverId}
                onReverseSelected={handleReverseSelected} routesForExport={routesForExport} />}
          </SmartMapDock>}
          <SmartMapDock id="smart-map-open-orders" label="Open orders" open={splitView} onToggle={toggleOpenOrders}>
            <OrderListPanel orders={mappedOrders} hiddenOrderIds={effectiveHiddenOrderIds} onToggle={toggleOrderVisibility}
              onShowAll={showAllOnMap} onRestore={restoreMapVisibility} onFocusOrder={id => {
                toggleOrderVisibility(id, true); setFocusedOrderId(id);
              }} />
          </SmartMapDock>
        </div>
      </div>
      </div>
    </div>
  );
};

export default OrderLocations;
